import { Box, Button, Collapse, UnstyledButton } from '@mantine/core';
import { useIntersection } from '@mantine/hooks';
import type { TranslateFn } from '@newo/localization';
import type { CreatorAgentSuccessFlowStepKey as AgentSuccessStep } from '@newo/tenant-metadata';
import { Info, LucideArrowRight } from 'lucide-react';
import { type FC, type MouseEvent, memo } from 'react';
import Title from '@/components/Title';
import VideoPlayer from '@/components/VideoPlayer';
import DarkScriptClipboard from '@/pages/AgentSuccess/components/StepsBlock/components/CollapseStepCard/components/DarkScriptClipboard';
import { partnerMetadata } from '@/services/PartnerMetadata';
import useAppStore from '@/store/app';
import {
	CardType,
	type TAgentSuccessStepCard
} from '@/types/partner-metadata.types.ts';
import { cn } from '@/utils';

type Props = {
	isExpanded: boolean;
	handleExpand: (step: AgentSuccessStep) => void;
	cardData: TAgentSuccessStepCard;
	isSelected?: boolean;
	step: AgentSuccessStep;
	handleActionClick?: (ev: MouseEvent<HTMLButtonElement>) => void;
	onInfoIconClick: (key: AgentSuccessStep) => void;
	isMiniCreatorMode: boolean;
	translate: TranslateFn;
};

const CollapseStepCard: FC<Props> = memo(
	({
		cardData,
		isSelected,
		handleActionClick,
		onInfoIconClick,
		step,
		isExpanded,
		handleExpand,
		isMiniCreatorMode,
		translate
	}) => {
		const { ref: stepRef, entry } = useIntersection({ threshold: 0.6 });

		const { type, icon: Icon, videoSrc, showInfo, learnMoreLink } = cardData;

		const agentData = useAppStore((state) => state.agentData);

		const scriptValue = agentData?.widget_script || '';

		const handleInfoClick = (ev: MouseEvent<HTMLButtonElement>) => {
			ev.stopPropagation();
			onInfoIconClick?.(step);
		};
		return (
			<Box
				className={cn(
					'flex flex-col w-full rounded-[15px] overflow-hidden bg-white',
					'!border !border-primary-900',
					'shadow-[0_24px_48px_rgba(18,18,23,0.03),_0_10px_18px_rgba(18,18,23,0.03),_0_5px_8px_rgba(18,18,23,0.04),_0_2px_4px_rgba(18,18,23,0.04)]',
					{ 'cursor-pointer': !isExpanded }
				)}
				data-testid={`agent-success-step-card-${step.toLowerCase()}`}
				onClick={() => handleExpand(step)}
				ref={stepRef}
				variant="transparent"
			>
				<div
					className={cn('p-6 flex items-center gap-[6px]', {
						'p-3 py-0': isMiniCreatorMode
					})}
				>
					<Title
						className={cn('text-primary-900 text-2xl font-semibold m-0', {
							'!text-base': isMiniCreatorMode,
							'xl:!text-3xl': !isMiniCreatorMode
						})}
						level={4}
						size="h5"
					>
						{translate(
							'creator',
							`agent_success.next_steps.${step.toLowerCase()}.title`
						)}
					</Title>

					{showInfo && (
						<UnstyledButton
							className="ml-[6px]"
							onClick={handleInfoClick}
						>
							<Info />
						</UnstyledButton>
					)}
				</div>

				<Collapse in={isExpanded}>
					<div
						className={cn('flex flex-col gap-6 p-6 pt-0', {
							'gap-3 p-3': isMiniCreatorMode
						})}
					>
						<span
							className={cn('text-black text-base font-medium opacity-50', {
								'text-sm ': isMiniCreatorMode
							})}
						>
							{translate(
								'creator',
								`agent_success.next_steps.${step.toLowerCase()}.subtitle`
							)}
						</span>

						{type === CardType.SCRIPT && (
							<DarkScriptClipboard scriptValue={scriptValue} />
						)}

						{learnMoreLink?.trim() ? (
							<Button
								className="!flex items-start max-w-fit"
								component="a"
								href={learnMoreLink}
								p={0}
								target="_blank"
								variant="transparent"
							>
								{translate(
									'creator',
									`agent_success.next_steps.${step.toLowerCase()}.learn_more_label`
								)}
								<LucideArrowRight />
							</Button>
						) : undefined}
						<Button
							className={cn(
								'!w-full flex items-center justify-center gap-[10px] rounded-[12px] text-[14px] font-medium border transition-colors',
								{
									'text-white hover:bg-[var(--button-hover)]':
										isSelected || partnerMetadata.isAlwaysFilledStepButton(),
									'bg-white text-black border-black':
										!isSelected && !partnerMetadata.isAlwaysFilledStepButton(),
									'md:max-w-fit': !isMiniCreatorMode
								}
							)}
							data-card-key={step}
							data-testid={`agent-success-step-action-${step.toLowerCase()}`}
							h={40}
							onClick={(ev) => {
								ev.stopPropagation();
								handleActionClick?.(ev);
							}}
							style={{
								borderRadius: `${partnerMetadata.getSubmitCtaRadius()}px`
							}}
						>
							{translate(
								'creator',
								`agent_success.next_steps.${step.toLowerCase()}.button_label`
							)}
						</Button>

						{videoSrc && (
							<Box className="overflow-hidden w-full rounded-[8px] bg-black">
								<VideoPlayer
									autoPlay={entry?.isIntersecting}
									className="w-full"
									source={videoSrc}
								/>
							</Box>
						)}

						{Icon && !videoSrc && !isMiniCreatorMode && (
							<Icon className="h-[159px] w-auto opacity-[0.05]" />
						)}
					</div>
				</Collapse>
			</Box>
		);
	}
);

export default CollapseStepCard;
